import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { twJoin } from "tailwind-merge";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { getDicoStatus, getHomeDnaStatus, getRemsStatus, getWunderbricksStatus } from "@/components/Ticket/helpers";
import type { TicketCommentPayload } from "@/components/Ticket/TicketCommentField/helpers";
import { TicketDicoCard } from "@/components/Ticket/TicketDetails/TicketDicoCard";
import { TicketHomeDnaCard } from "@/components/Ticket/TicketDetails/TicketHomeDnaCard";
import { TicketInfo } from "@/components/Ticket/TicketDetails/TicketInfoHeader";
import { TicketLog } from "@/components/Ticket/TicketDetails/TicketLog";
import { TicketRemsCard } from "@/components/Ticket/TicketDetails/TicketRemsCard";
import { TicketReporterCard } from "@/components/Ticket/TicketDetails/TicketReporterCard";
import { TicketWunderbricksCard } from "@/components/Ticket/TicketDetails/TicketWunderbricksCard";
import { TicketStatusChangeModal } from "@/components/Ticket/TicketStatusChangeModal";
import { Stack } from "@/design-system/Stack";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useQuickReplyManager } from "@/providers/QuickReplyProvider";
import { StartupLoading } from "@/providers/StartupLoading";
import { useTicketCommentHandler } from "@/queries/tickets/helpers";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { QuickReplyMenuBar } from "../components/QuickReplyMenuBar";

export function QuickReplyTicketPage(): React.ReactNode {
  const { t } = useTranslation();
  const [isChangeStatusModalOpened, changeStatusModalHandler] = useBool();
  const [isActivitiesDescending, descendingActivitiesHandlers] = useBool(false);

  const [headerParam] = useQueryParam("header");
  const { entityId: ticketId } = useQuickReplyManager();
  const projectId = useProjectId();
  const sessionUser = useSessionUser();

  const ticketQueries = useTicketQueries();
  const {
    data: ticket,
    isPending: isPendingTicket,
    error: errorTicket,
  } = useQuery({
    ...ticketQueries.detailsAdmin(ticketId),
    enabled: Boolean(ticketId),
  });
  const activitiesQuery = useInfiniteQuery(
    ticketQueries.activitiesInfinite({
      ticketId,
      sortDescending: isActivitiesDescending,
    }),
  );
  const activities = useMemo(
    () => activitiesQuery.data?.pages.flatMap((x) => x.items) || [],
    [activitiesQuery.data?.pages],
  );

  const { createComment, editComment } = useTicketCommentHandler(ticketId);
  const updateStatusMutation = ticketMutations.useUpdateStatus();

  const statuses = useMemo(
    () =>
      ticket?.possibleStatuses.map((status) => ({
        id: status.id,
        description: status.name,
        labelColor: status.color,
        defaultStatusId: status.type,
      })) || [],
    [ticket?.possibleStatuses],
  );

  const ticketTitle = ticket?.title;
  const tabTitle = (!errorTicket && ticketTitle?.trim()) || t("page.tickets.details.title");
  useDocumentTitle(tabTitle);

  if (isPendingTicket) {
    return <StartupLoading />;
  }

  if (errorTicket) {
    return <ErrorPage status={(errorTicket as unknown as Response)?.status || 0} />;
  }

  async function onCreateComment(payload: TicketCommentPayload) {
    if (!ticket) {
      return;
    }

    await createComment({
      ...payload,
      fetchAllActivities: !isActivitiesDescending,
    });

    const isStatusNewOrRejected = ["new", "rejected"].includes(ticket.status.type);

    if (payload.type !== "internal" && isStatusNewOrRejected && ticket.canChangeStatus) {
      changeStatusModalHandler.setTrue();
    }
  }

  const getLoginUrl = () => {
    if (ticket.requesterHasMultipleProjects) {
      const openTicketValue = btoa(`${projectId}|${ticket.id}`);

      return {
        pathname: routes.portfolio.tickets(),
        search: `openTicket=${openTicketValue}`,
      };
    }

    return routes.tickets.details({
      id: ticket.id,
      slug: sessionUser.project.slug,
    });
  };

  const showHeader = headerParam !== "false";
  const rems = getRemsStatus(ticket);
  const homeDna = getHomeDnaStatus(ticket);
  const dico = getDicoStatus(ticket);
  const wunderbricks = getWunderbricksStatus(ticket);

  return (
    <>
      {showHeader && <QuickReplyMenuBar user={sessionUser} loginUrl={getLoginUrl()} />}

      <main className={twJoin("mx-auto w-full max-w-xl p-space-md", showHeader && "pb-page-bottom")}>
        <PageGrid.Row>
          <PageGrid.Column size="66%" isDesktopIndifferent>
            <Stack gap="md">
              <TicketInfo ticket={ticket} showProject={showHeader} showDecisionTreePath />
              <TicketLog
                isActivitiesDescending={isActivitiesDescending}
                onToggleActivitySorting={descendingActivitiesHandlers.toggle}
                isFetchingMoreActivities={activitiesQuery.isFetchingNextPage || activitiesQuery.isPending}
                fetchMoreActivities={activitiesQuery.fetchNextPage}
                hasMoreActivities={activitiesQuery.hasNextPage || false}
                onCreateComment={onCreateComment}
                onEditComment={editComment}
                {...{ activities, ticket, sessionUser }}
              />
            </Stack>
          </PageGrid.Column>
          <PageGrid.Column size="33%" isDesktopIndifferent>
            <aside className="flex w-full flex-col gap-space-md">
              <TicketReporterCard ticket={ticket} />
              {rems && (
                <TicketRemsCard ticketId={ticket.id} ticketIsCollective={ticket.visibility !== "private"} rems={rems} />
              )}
              {homeDna && <TicketHomeDnaCard ticket={ticket} homeDna={homeDna} />}
              {dico ? <TicketDicoCard ticket={ticket} dico={dico} /> : null}
              {wunderbricks && <TicketWunderbricksCard ticketId={ticket.id} wunderbricks={wunderbricks} />}
            </aside>
          </PageGrid.Column>
        </PageGrid.Row>
      </main>

      <TicketStatusChangeModal
        isOpened={isChangeStatusModalOpened}
        onOpenChange={changeStatusModalHandler.set}
        onSubmit={async (status) => {
          try {
            if (status.id !== ticket.status.id) {
              await updateStatusMutation.mutateAsync({
                ticketId: ticket.id,
                data: {
                  statusId: status.id,
                  silent: true,
                },
              });
            }
          } finally {
            changeStatusModalHandler.setFalse();
          }
        }}
        targetStatus={statuses[0]}
        possibleStatuses={statuses}
      />
    </>
  );
}
